import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CompanyResponse } from '@shared/schemas';

import type { TeamMember } from '../../_shared/domain/company-detail.model';
import { emptyEnrich, initialState } from './company-detail.state';

/**
 * Admin Company Detail slice. Reducers are pure state transitions only; the
 * initial load, the lazy team load and the enrich POST + poll all live in the
 * saga, which dispatches these `load*` / `teamLoad*` / `enrich*` transitions as it
 * progresses. `loadStarted` keeps the current `company` so a re-load never blanks
 * the page.
 */
const slice = createSlice({
  name: 'companyDetail',
  initialState,
  reducers: {
    // ── Company load lifecycle ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<CompanyResponse>) => {
      state.company = action.payload;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    // ── Team lazy-load lifecycle ──
    teamLoadStarted: (state) => {
      state.teamStatus = 'loading';
    },
    teamLoadSucceeded: (state, action: PayloadAction<TeamMember[]>) => {
      state.team = action.payload;
      state.teamStatus = 'loaded';
    },
    teamLoadFailed: (state) => {
      state.team = [];
      state.teamStatus = 'failed';
    },

    // ── Enrichment lifecycle ──
    enrichStarted: (state) => {
      state.enrich = { status: 'enriching', error: null };
    },
    /** POST accepted: now polling for fresher data (drives the "started" snackbar). */
    enrichAccepted: (state) => {
      state.enrich = { status: 'polling', error: null };
    },
    /** Poll saw fresher data: patch the loaded company in place. */
    enrichSucceeded: (state, action: PayloadAction<CompanyResponse>) => {
      state.company = action.payload;
      state.enrich = { status: 'succeeded', error: null };
    },
    /** Poll gave up without seeing new data; not an error, just stop the spinner. */
    enrichTimedOut: (state) => {
      state.enrich = { status: 'timedout', error: null };
    },
    enrichFailed: (state, action: PayloadAction<string>) => {
      state.enrich = { status: 'failed', error: action.payload };
    },
    /** Acknowledge a settled enrichment (page calls after handling the outcome). */
    enrichCleared: (state) => {
      state.enrich = emptyEnrich;
    },

    /** Reset everything to the initial state (on mount + unmount). */
    resetForm: () => initialState,
  },
});

export const companyDetailActions = slice.actions;
export const companyDetailReducer = slice.reducer;
